import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { crmSupabase } from "../../lib/crm-client";

type EstrellaRow = {
  id: string;
  full_name: string;
  status: string;
  last_contact_at: string | null;
  last_response_at: string | null;
  estrella_since: string | null;
  category: { name: string; color_hex: string } | null;
};

export default function CrmEstrellas() {
  const [rows, setRows] = useState<EstrellaRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "responded" | "no_response">("all");

  useEffect(() => {
    if (!crmSupabase) {
      setLoading(false);
      return;
    }
    crmSupabase
      .from("lead")
      .select(
        "id,full_name,status,last_contact_at,last_response_at,estrella_since,category:category_id(name,color_hex)",
      )
      .eq("is_estrella", true)
      .eq("is_test", false)
      .order("last_contact_at", { ascending: false, nullsFirst: false })
      .then(({ data, error }) => {
        if (error) console.error(error);
        setRows((data as any[]) ?? []);
        setLoading(false);
      });
  }, []);

  const filtered = rows.filter((r) => {
    if (filter === "responded") return r.status === "responded";
    if (filter === "no_response") return !r.last_response_at;
    return true;
  });

  if (loading) {
    return (
      <main className="px-10 py-8 max-w-[1200px] mx-auto font-mono text-sm text-concrete">
        Chargement…
      </main>
    );
  }

  return (
    <main className="px-10 py-8 max-w-[1200px] mx-auto">
      <header className="mb-6 flex items-baseline justify-between">
        <h2 className="font-sans text-4xl font-bold tracking-tight text-charcoal">
          Estrellas
        </h2>
        <span className="font-mono text-xs uppercase text-concrete">
          {rows.length} leads
        </span>
      </header>

      <div className="flex gap-2 mb-4">
        {(
          [
            ["all", `Toutes (${rows.length})`],
            ["responded", `Ont répondu (${rows.filter((r) => r.status === "responded").length})`],
            ["no_response", `Sans réponse (${rows.filter((r) => !r.last_response_at).length})`],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`px-3 py-1.5 rounded-full text-xs font-mono uppercase border transition ${
              filter === key
                ? "bg-charcoal text-ivory border-charcoal"
                : "bg-white text-charcoal border-stone hover:border-charcoal"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <ul className="flex flex-col gap-2">
        {filtered.map((r) => (
          <li
            key={r.id}
            className="bg-white border border-stone rounded-lg px-4 py-3 grid grid-cols-[auto_1fr_auto] gap-3 items-center"
          >
            <span className="text-lemonade text-lg" aria-label="Estrella">
              ★
            </span>
            <div className="flex flex-col gap-0.5 min-w-0">
              <div className="font-semibold text-sm flex items-baseline gap-2 flex-wrap">
                <Link
                  to={`/crm/leads/${r.id}`}
                  className="text-charcoal hover:underline"
                >
                  {r.full_name}
                </Link>
                {r.category && (
                  <span
                    className="font-mono text-[10px] uppercase px-1.5 py-0.5 rounded text-white"
                    style={{ backgroundColor: r.category.color_hex }}
                  >
                    {r.category.name}
                  </span>
                )}
                {r.status === "responded" && (
                  <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 rounded bg-lemonade text-charcoal">
                    A RÉPONDU
                  </span>
                )}
              </div>
              <div className="flex gap-3 font-mono text-[10px] text-concrete uppercase mt-1">
                <span>Dernier contact : {r.last_contact_at?.slice(0, 10) ?? "—"}</span>
                <span>Dernière réponse : {r.last_response_at?.slice(0, 10) ?? "—"}</span>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </main>
  );
}
